import {
  Grid,
  Card,
  Icon,
  CircularProgress,
  MenuItem,
  FormControl,
  IconButton,
  Avatar,
} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import InputAdornment from "@mui/material/InputAdornment";
import DataTable from "components/DataTable";
import PageHeader from "components/PageHeader";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import useContributionData from "layouts/tables/data/contributionTableData";
import { API_HOST } from "config/api";

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  if (path.startsWith("blob")) return path;
  return `${API_HOST}/storage/${path}`;
};

function Contributions() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedPayment,
    setSelectedPayment,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddPayment,
    projects,
    searchTerm,
    setSearchTerm,
    filteredMembers,
    isLoadingMembers,
    showMemberDropdown,
    setShowMemberDropdown,
    handleMemberSelect,
    isGroupContribution,
    setIsGroupContribution,
    contributorName,
    setContributorName,
  } = useContributionData();

  if (isLoading) {
    return (
      <Box pt={6} pb={3}>
        <Typography>Loading contributions...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={6} pb={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <PageHeader
                title="Contributions"
                subtitle="Ad-hoc contributions — from a member, or a lump sum collected together at a gathering."
                actions={
                  <>
                    <Button variant="contained" onClick={handleAddPayment}>
                      <Icon>add</Icon>&nbsp;New Contribution
                    </Button>
                    <Button variant="contained" onClick={handlePrint}>
                      <Icon>print</Icon>
                    </Button>
                  </>
                }
              />
              <Box pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search contributions..."
                />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedPayment?.id ? "Edit Contribution" : "Record Contribution"}
        onSave={handleEditSubmit}
        saveLabel={selectedPayment?.id ? "Save Changes" : "Record Contribution"}
        saveDisabled={
          (isGroupContribution ? !contributorName.trim() : !selectedPayment?.member_id) ||
          !selectedPayment?.amount
        }
        maxWidth="md"
      >
        <FormSection>
          <FormControlLabel
            control={
              <Switch
                checked={isGroupContribution}
                onChange={(e) => setIsGroupContribution(e.target.checked)}
              />
            }
            label="Group / collective contribution (no specific member)"
            sx={{ mb: 1 }}
          />

          {isGroupContribution ? (
            <TextField
              label="Description"
              placeholder="e.g. Sunday service collection"
              value={contributorName}
              onChange={(e) => setContributorName(e.target.value)}
              fullWidth
              margin="normal"
              required
              helperText="Who or what gathering this lump-sum contribution came from."
            />
          ) : (
            <FormControl fullWidth>
              <TextField
                label="Search Member"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowMemberDropdown(e.target.value.trim() !== "");
                }}
                onFocus={() => {
                  if (searchTerm.trim() !== "" && filteredMembers.length > 0) {
                    setShowMemberDropdown(true);
                  }
                }}
                onBlur={() => setTimeout(() => setShowMemberDropdown(false), 200)}
                fullWidth
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <>
                      {searchTerm && !isLoadingMembers && (
                        <IconButton
                          onClick={() => {
                            setSearchTerm("");
                            setSelectedPayment((prev) => ({
                              ...prev,
                              member_id: "",
                              member_name: "",
                              memberId: "",
                              memberPhoto: "",
                            }));
                          }}
                          size="small"
                        >
                          <Icon>clear</Icon>
                        </IconButton>
                      )}
                      {isLoadingMembers && <CircularProgress color="inherit" size={20} />}
                    </>
                  ),
                }}
              />
              {showMemberDropdown && (
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1300,
                    width: "100%",
                    maxHeight: 250,
                    overflowY: "auto",
                    bgcolor: "background.paper",
                    boxShadow: 3,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mt: 0.5,
                  }}
                >
                  {filteredMembers.length > 0 ? (
                    filteredMembers.map((member) => (
                      <MenuItem
                        key={member.id}
                        onClick={() => handleMemberSelect(member)}
                        sx={{ py: 1, px: 2, display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          src={getImageUrl(member.photo)}
                          sx={{ width: 36, height: 36 }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "";
                          }}
                        >
                          {!member.photo && <Icon>person</Icon>}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {member.first_name} {member.surname}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {member.memberID}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled sx={{ py: 1, px: 2 }}>
                      {isLoadingMembers ? "Loading..." : "No members found"}
                    </MenuItem>
                  )}
                </Box>
              )}
            </FormControl>
          )}
        </FormSection>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormSection sx={{ flex: 1, minWidth: 240 }}>
            <TextField
              label="Amount"
              type="number"
              value={selectedPayment?.amount || ""}
              onChange={(e) => setSelectedPayment({ ...selectedPayment, amount: e.target.value })}
              fullWidth
              margin="normal"
              required
              InputProps={{ startAdornment: <InputAdornment position="start">GHS</InputAdornment> }}
            />
            <TextField
              select
              label="Linked Project (optional)"
              value={selectedPayment?.project_id || ""}
              onChange={(e) =>
                setSelectedPayment({ ...selectedPayment, project_id: e.target.value })
              }
              fullWidth
              margin="normal"
            >
              <MenuItem value="">None — general contribution</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>
          </FormSection>

          <FormSection sx={{ flex: 1, minWidth: 240 }}>
            <TextField
              select
              label="Payment Method"
              value={selectedPayment?.method || "manual"}
              onChange={(e) => setSelectedPayment({ ...selectedPayment, method: e.target.value })}
              fullWidth
              margin="normal"
              required
            >
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="paystack">Paystack</MenuItem>
            </TextField>
            <TextField
              label="Payment Date"
              type="date"
              value={selectedPayment?.paymentDate || new Date().toISOString().split("T")[0]}
              onChange={(e) =>
                setSelectedPayment({ ...selectedPayment, paymentDate: e.target.value })
              }
              fullWidth
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
          </FormSection>
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message="Are you sure you want to delete this contribution?"
        confirmLabel="Confirm Delete"
      />
    </Box>
  );
}

export default Contributions;
